import React, { useState } from "react";
import { FaDownload } from "react-icons/fa";
import { IoClose, IoExpand, IoContract, IoDownload } from "react-icons/io5";
import { getFormattedTime } from "../utils";
import basil_document_solid from "../../assets/images/basil_document-solid.png";
import PdfMessage from "../../components/ChatInterface/PdfMessage";
import customerImage from "../../assets/images/customer_avatar.png";

const API_BASE_URL = "http://image.onmls.com";

// Helper function to get receiver's initials
const getReceiverInitials = (item, sellerDetails, profileData) => {
    // Try to get receiver's name from various sources
    const receiverName = item?.receiver_user_name || sellerDetails?.receiver_user_name || sellerDetails?.owner_name || '';
    const firstName = item?.first_name || sellerDetails?.first_name || '';
    const lastName = item?.last_name || sellerDetails?.last_name || '';

    if (firstName && lastName) {
        return `${firstName[0]?.toUpperCase() || ''}${lastName[0]?.toUpperCase() || ''}`;
    }

    if (receiverName) {
        const nameParts = receiverName.split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0][0]?.toUpperCase() || ''}${nameParts[1][0]?.toUpperCase() || ''}`;
        }
        return receiverName[0]?.toUpperCase() || '?';
    }

    return '?';
};

export default function MessageItem({ item, index, profileData, onDownloadFile, filterMessage, isCustomerSupport, sellerDetails }) {
    const [showImageModal, setShowImageModal] = useState(false);
    const [modalImageUrl, setModalImageUrl] = useState("");
    const [isZoomed, setIsZoomed] = useState(false);
    const [imageScale, setImageScale] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const isCurrentUser = item?.sender_id === profileData?.id;
    const [receiverImageError, setReceiverImageError] = useState(false);
    const getReceiverImage = () => {
        return item?.user_id !== profileData?.id ? API_BASE_URL + item?.user_profile_image : API_BASE_URL + item?.receiver_user_profile_image;
    };
    const receiverImage = isCustomerSupport ? customerImage : getReceiverImage();
    const hasValidReceiverImage = receiverImage && !receiverImage.endsWith('null') && !receiverImage.endsWith('undefined') && !receiverImageError;

    const handleImageClick = (imageUrl) => {
        setModalImageUrl(imageUrl);
        setShowImageModal(true);
        setImageScale(1);
        setImagePosition({ x: 0, y: 0 });
        setIsZoomed(false);
    };

    const handleCloseModal = () => {
        setShowImageModal(false);
        setImageScale(1);
        setImagePosition({ x: 0, y: 0 });
        setIsZoomed(false);
        setIsDragging(false);
    };

    const handleZoomToggle = () => {
        if (isZoomed) {
            setImageScale(1);
            setImagePosition({ x: 0, y: 0 });
            setIsZoomed(false);
        } else {
            setImageScale(2);
            setIsZoomed(true);
        }
    };

    const handleMouseDown = (e) => {
        if (isZoomed) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - imagePosition.x,
                y: e.clientY - imagePosition.y,
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && isZoomed) {
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y,
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleDownloadImage = () => {
        const link = document.createElement("a");
        link.href = modalImageUrl;
        link.download = `image_${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div
            key={index}
            className="tw-text-[14px] tw-w-full tw-flex tw-p-[10px]"
            style={{
                justifyContent: isCurrentUser ? "flex-end" : "flex-start",
            }}
            id={`message-${item.id}`}
        >
            {/* Profile Picture */}
            {!isCurrentUser && (
                hasValidReceiverImage ? (
                    <img
                        src={receiverImage}
                        alt="profile"
                        onError={() => setReceiverImageError(true)}
                        className="tw-w-[36px] tw-h-[36px] tw-rounded-full tw-mr-2 tw-object-cover"
                    />
                ) : (
                    <div className="tw-w-[36px] tw-h-[36px] tw-bg-primary tw-rounded-full tw-mr-2 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-text-white">
                        {getReceiverInitials(item, sellerDetails, profileData)}
                    </div>
                )
            )}

            <div style={{ maxWidth: "70%" }}>
                <div
                    style={{
                        backgroundColor: isCurrentUser ? "#1ABC9C" : "#fff",
                        borderRadius: item?.media || item?.message?.startsWith("/media/") ? 15 : 50,
                        padding: 10,
                    }}
                >
                    {item?.media ? (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            {!item.media.startsWith("/documents/") ? (
                                <>
                                    <img
                                        src={API_BASE_URL + item?.media}
                                        alt="media"
                                        onClick={() => handleImageClick(API_BASE_URL + item?.media)}
                                        className="tw-cursor-pointer tw-transition-transform tw-duration-200 hover:tw-scale-105"
                                        style={{
                                            width: 130,
                                            height: 130,
                                            borderRadius: 10,
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <PdfMessage
                                        fileName={item.media.split("/").pop()}
                                        fileSize={"32KB"}
                                        pdfUrl={"https://admin.onmls.com/api" + item?.media}
                                    />
                                </>
                            )}
                        </div>
                    ) : item?.message?.startsWith("/media/") || item?.message?.startsWith("/documents/") ? (
                        <>
                            <PdfMessage
                                fileName={item.message.split("/").pop()}
                                fileSize={"32KB"}
                                pdfUrl={"https://admin.onmls.com/api" + item?.message}
                            />
                        </>
                    ) : (
                        <p
                            className="tw-flex tw-flex-col tw-gap-2 tw-px-4"
                            style={{
                                color: isCurrentUser ? "#fff" : "#000",
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                maxWidth: "100%",
                            }}
                        >
                            <span>{filterMessage(item?.message)}</span>
                        </p>
                    )}
                </div>
                <span className="tw-w-full tw-flex tw-justify-end">
                    {item.sent_at ? getFormattedTime(item.sent_at) : getFormattedTime(new Date())}
                </span>
            </div>

            {/* Profile Picture for current user */}
            {isCurrentUser &&
                (profileData?.profile_pic ? (
                    <img
                        src={profileData?.profile_pic} // fallback to default
                        alt="profile"
                        onError={(e) => (e.target.src = customerImage)}
                        className="tw-w-[36px] tw-h-[36px] tw-rounded-full tw-ml-2"
                    />
                ) : (
                    // use react-icons
                    <div className="tw-w-[36px] tw-h-[36px] tw-bg-primary tw-rounded-full tw-ml-2 tw-flex tw-items-center tw-justify-center tw-text-lg tw-font-bold tw-text-gray-700">
                        {profileData?.first_name[0]}
                        {profileData?.last_name[0]}
                    </div>
                ))}
            {showImageModal && (
                <div
                    className="tw-fixed tw-inset-0 tw-bg-black/80 tw-backdrop-blur-sm tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300"
                    onClick={handleCloseModal}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    style={{ animation: "fadeIn 0.3s ease-out" }}
                >
                    {/* Enhanced Modal Container */}
                    <div
                        className="tw-relative tw-max-w-[95vw] tw-min-w-[80vw] tw-max-h-[95vh] tw-min-h-[80vh] tw-flex tw-flex-col tw-items-center"
                        onClick={(e) => e.stopPropagation()}
                        style={{ animation: "slideIn 0.3s ease-out" }}
                    >
                        {/* Header with controls */}
                        <div className="tw-absolute tw-top-4 tw-left-0 tw-right-0 tw-flex tw-justify-between tw-items-center tw-px-6 tw-z-10">
                            <div className="tw-flex tw-gap-3">
                                <button
                                    onClick={handleZoomToggle}
                                    className="tw-bg-black/50 tw-text-white tw-p-3 tw-rounded-full tw-backdrop-blur-sm tw-transition-all tw-duration-200 hover:tw-bg-black/70 hover:tw-scale-110"
                                    title={isZoomed ? "Zoom Out" : "Zoom In"}
                                >
                                    {isZoomed ? <IoContract size={20} /> : <IoExpand size={20} />}
                                </button>
                                <button
                                    onClick={handleDownloadImage}
                                    className="tw-bg-black/50 tw-text-white tw-p-3 tw-rounded-full tw-backdrop-blur-sm tw-transition-all tw-duration-200 hover:tw-bg-black/70 hover:tw-scale-110"
                                    title="Download Image"
                                >
                                    <IoDownload size={20} />
                                </button>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="tw-bg-black/50 tw-text-white tw-p-3 tw-rounded-full tw-backdrop-blur-sm tw-transition-all tw-duration-200 hover:tw-bg-red-500/70 hover:tw-scale-110"
                                title="Close"
                            >
                                <IoClose size={20} />
                            </button>
                        </div>

                        {/* Image Container */}
                        <div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-min-h-[70vh] tw-min-w-[70vh] tw-flex tw-items-center tw-justify-center tw-shadow-2xl tw-bg-white/10 tw-backdrop-blur-sm tw-p-2">
                            <img
                                src={modalImageUrl}
                                alt="Preview"
                                className={`tw-max-w-[90vw] tw-max-h-[85vh] tw-object-contain tw-rounded-xl tw-transition-transform tw-duration-300 ${
                                    isZoomed ? "tw-cursor-grab" : "tw-cursor-zoom-in"
                                } ${isDragging ? "tw-cursor-grabbing" : ""}`}
                                style={{
                                    transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                                    transformOrigin: "center center",
                                }}
                                onMouseDown={handleMouseDown}
                                onDoubleClick={handleZoomToggle}
                                draggable={false}
                            />
                        </div>

                        {/* Info bar */}
                        <div className="tw-absolute tw-bottom-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-black/50 tw-text-white tw-px-4 tw-py-2 tw-rounded-full tw-backdrop-blur-sm tw-text-sm">
                            {isZoomed ? "Click and drag to pan • Double-click to zoom out" : "Double-click to zoom in"}
                        </div>
                    </div>

                    <style jsx>{`
                        @keyframes fadeIn {
                            from {
                                opacity: 0;
                            }
                            to {
                                opacity: 1;
                            }
                        }

                        @keyframes slideIn {
                            from {
                                opacity: 0;
                                transform: scale(0.9) translateY(20px);
                            }
                            to {
                                opacity: 1;
                                transform: scale(1) translateY(0);
                            }
                        }
                    `}</style>
                </div>
            )}
        </div>
    );
}
